import { EditorSettings } from "../../types/EditorSettings";
import { ViewType } from "../../types/ViewType";
import { isMobile } from "../../utils/breakpoints";
import { createArrayCaster } from "../../utils/createArrayCaster";

// Do not remove items from this list, even after the view is deprecated
// because it may be referenced in a user's cookie. If the cookie can't be
// parsed, the rest of the user's settings won't be respected.
const ALL_VIEW_NAMES = [
  ViewType.SIDEBAR,
  ViewType.DATA_PANE,
  ViewType.COMPETITIONS_PANE,
  ViewType.MODELS_PANE,
  ViewType.SETTINGS_PANE,
  ViewType.DEBUG_PANE,
  ViewType.JUPYTER_DEV_TOOLS,
  ViewType.JUPYTER_DEV_TOOLS_EVENTS_PANE,
  ViewType.JUPYTER_DEV_TOOLS_REDUX_PANE,
  ViewType.INTERACTIVE_SESSIONS,
  ViewType.COMMIT_SESSIONS,
  ViewType.SNIPPETS_PANE,
  ViewType.SCHEDULE_PANE,
];

const DEFAULT_USER_VIEWS: EditorSettings = {
  // The default views that are enabled.
  enabledViews: [
    ViewType.SIDEBAR,
    ViewType.DATA_PANE,
    ViewType.MODELS_PANE,
    ViewType.COMPETITIONS_PANE,
    ViewType.SETTINGS_PANE,
    ViewType.SNIPPETS_PANE,
    ViewType.SCHEDULE_PANE,
  ],
  // The default views that are expanded if enabled.
  expandedViews: [
    ViewType.SIDEBAR,
    ViewType.DATA_PANE,
    ViewType.MODELS_PANE,
    ViewType.DEBUG_PANE,
    ViewType.SNIPPETS_PANE,
  ],
};

const castViewNames = createArrayCaster(ALL_VIEW_NAMES);

// TODO(cochamberlain): Rewrite as shallowMergeObject.
/** Merges partial editor settings with defaults. */
export const createEditorSettings = (
  editorSettings: Partial<EditorSettings> = {}
): EditorSettings => {
  // Optional enabled views are only available to admins.
  const optionalEnabledViews = castViewNames(editorSettings.enabledViews) || [];
  const enabledViews = DEFAULT_USER_VIEWS.enabledViews
    .concat(optionalEnabledViews)
    .filter((value, index, self) => self.indexOf(value) === index);

  const defaultSettings = {
    enabledViews,
    expandedViews:
      // Attempt to cast the old device settings with the new ones or
      // reset them if the format is no longer supported.
      castViewNames(editorSettings.expandedViews) ||
      DEFAULT_USER_VIEWS.expandedViews,
  };

  // In mobile view, collapse the sidebar by default, no matter what's in local storage.
  if (isMobile()) {
    return {
      enabledViews: defaultSettings.enabledViews,
      expandedViews: defaultSettings.expandedViews.filter(
        v => v !== ViewType.SIDEBAR
      ),
    };
  }
  return defaultSettings;
};
